/**
 * RsvStat — Tier 0 slice-local primitive (epic SCRUM-332 / SCRUM-333).
 *
 * One cell of a reservation hero/summary strip: an uppercase micro-label
 * over a value. Presentational only — the caller supplies an already
 * formatted value node (a `<LedgerLine>`-style money string, a date, a
 * `<ResStatusPill>`, etc.).
 *
 * **Design source.** `design_handoff_reservations/` reservation detail
 * hero — VISUAL reference only (the bundle's data model is STALE; this
 * primitive carries no data assumptions, only layout + type). Mirrors
 * `property/_components/Stat` in intent; kept slice-local rather than
 * imported cross-module (Hybrid Z / ADR frontend-0001 forbids reaching
 * into another slice's `_components/`).
 *
 * **Tokens (ADR frontend-0003).** Label + value colors come from semantic
 * `--fg-*` tokens only; geometry is Tailwind utilities. No hex, no bare
 * inline numerics (SCRUM-78/79) — letter-spacing is a token-free CSS
 * length string, matching the `property/_components/Stat` precedent.
 *
 * **Tier (ADR frontend-0004).** Tier 0: slice-local, NOT barrel-exported.
 * Promote on the second-consumer trigger (a second slice adopting the
 * same label/value cell).
 */

import * as React from 'react';

export interface RsvStatProps {
  /** Uppercase micro-label (rendered small + tracked-out). */
  readonly label: string;
  /** Pre-formatted value node (string, money line, pill, …). */
  readonly value: React.ReactNode;
}

export function RsvStat({ label, value }: RsvStatProps): React.ReactElement {
  return (
    <div>
      <div
        className="mb-1 text-xs font-bold uppercase text-[color:var(--fg-tertiary)]"
        style={{ letterSpacing: '0.07em' }}
      >
        {label}
      </div>
      <div className="text-sm font-semibold leading-tight text-[color:var(--fg-primary)]">
        {value}
      </div>
    </div>
  );
}
